import React from 'react';
import { PATHS } from '@/routes/paths';
import { useSession } from '@/hooks/useSession';
import { usePlatformAbilities } from '@/hooks/usePlatformAbilities';
import { TiaBanner } from '@/components/custom/tia-banner';
import { HomeNavCard } from '@/pages/dashboard/home/home-nav-card';
import { isPlayfulTheme } from '@/lib/layout-version';
import { greetingForTeacher } from './teacher-greeting';
import { JOINHA } from './teacher-character';
import { NextClassBanner } from './next-class-banner';
import { AttentionItems } from './attention-items';
import turmasIcon from '@/assets/images/icons/projetos.png';

// Tints pastéis dos nav-cards — mesmos tokens da home do aluno, pra as duas
// homes ficarem visualmente irmãs.
const NAV_ACCENTS = {
  turmas: { bg: 'var(--color-bg-lilac-soft)', arrow: 'var(--color-secondary)' }
};

// Banner de saudação sem personagem — usado enquanto a primeira pose não existe.
// Espelha o balão do TiaBanner pra o layout não mudar quando a arte entrar.
function GreetingFallback({ text }) {
  return (
    <div
      className="relative flex h-full flex-col gap-3 rounded-[24px] p-4 shadow-card"
      style={{ background: 'var(--color-bg-lilac)' }}
    >
      <div
        className="relative z-10 flex min-w-0 flex-1 items-start gap-3 rounded-[16px] rounded-tl-none bg-white p-4 shadow-card md:p-5"
        style={{ maxWidth: 400 }}
      >
        <p
          className="mb-0 whitespace-pre-line text-sm font-bold leading-relaxed"
          style={{ color: '#222731' }}
        >
          {text}
        </p>
      </div>
    </div>
  );
}

export function DashboardTeacherHero({ banner }) {
  const { session } = useSession();
  const { abilities } = usePlatformAbilities();

  const firstName = (session?.user?.name || '').trim().split(' ')[0];
  const welcomeMessage = greetingForTeacher(new Date().getDay(), firstName);

  // Plano de aula, Desafio ao vivo, Atividades e Avaliações saíram por hora —
  // eram só atalho pro que o menu/bottom-bar já oferece (decisão da usuária
  // ao ver a Home ao vivo). Minhas turmas fica: não tem equivalente direto
  // no menu do professor.
  //
  // O filtro logo abaixo é por truthy, então `available` precisa ser booleano
  // explícito: `abilities?.x` pode vir undefined e derrubar o card em silêncio.
  const navCards = [
    {
      key: 'turmas',
      to: PATHS.CLASSROOM.LIST_TURMAS,
      illustration: turmasIcon,
      title: 'Minhas turmas',
      subtitle: 'Acompanhe seus alunos',
      accent: NAV_ACCENTS.turmas,
      available: Boolean(abilities?.turmas)
    }
  ].filter(card => card.available);

  return (
    <div className="relative mb-6 flex flex-col gap-6">
      {/* 1. Saudação do personagem. O mt no md+ dá espaço pra cabeça estourar
          pra cima do card sem cobrir o breadcrumb — mesmo truque da home do
          aluno. Sem pose ainda, o fallback não estoura, então o mt só entra
          quando a arte existir. */}
      <div className={JOINHA ? 'relative sm:mt-20' : 'relative'}>
        {JOINHA ? (
          <TiaBanner
            className="h-full"
            image={JOINHA}
            imageAlt="Personagem do professor"
            imageHeight={210}
            imageOffset={200}
            bubbleCorner="tl"
            bubbleMaxWidth={400}
            text={welcomeMessage}
          />
        ) : (
          <GreetingFallback text={welcomeMessage} />
        )}
      </div>

      {banner}

      {isPlayfulTheme() && (
        <div className="relative flex flex-col gap-6 xl:flex-row xl:items-stretch">
          <NextClassBanner className="xl:flex-1" />
          <AttentionItems className="xl:flex-1" />
        </div>
      )}

      {/* 5. Nav-cards das frentes do professor. */}
      {navCards.length > 0 && (
        <div className="relative grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {navCards.map(card => (
            <HomeNavCard
              key={card.key}
              to={card.to}
              illustration={card.illustration}
              title={card.title}
              subtitle={card.subtitle}
              accent={card.accent}
            />
          ))}
        </div>
      )}
    </div>
  );
}
